/**
 * Refer a friend (v1.5.0, docs/v150-spec.md §2.8, §3.7): the friend gets a
 * free 30-day Premium pass; the referrer gets a thank-you notice.
 *
 * Codes are 8 characters from ABCDEFGHJKMNPQRSTUVWXYZ23456789 (no 0/O, 1/I/L).
 * `normalisePassCode` accepts whatever a person types or pastes — a raw code
 * in any case with spaces or hyphens, the https://…/pass/<code> link, the
 * babytrack://pass/<code> link, or the whole share message — and returns the
 * code or null.
 *
 * A code that arrives before its owner can use it (the babytrack:// link on a
 * signed-out app, the "Get started" button on /pass/:code) is stashed in
 * localStorage and redeemed once signed in: by the onboarding set-up step, or
 * by usePendingPass in the app shell. Sign-out and account deletion clear it.
 */

import { format } from 'date-fns';
import { silentFetchDetailed } from '../../shared/api/client';
import { isOffline } from '../../services/network';

export const PASS_DAYS = 30;
export const APP_STORE_URL = 'https://apps.apple.com/app/id6762176711';
export const PASS_STASH_KEY = 'bt_pending_pass';
// A stashed code older than this is dropped rather than applied to whoever
// signs in next on a shared device.
const STASH_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const CODE_RE = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{8}$/;
const LINK_RE = /\bpass\/([A-Za-z0-9-]+)/i;

/** The custom-scheme link that opens the app on this code. */
export const passAppLink = (code) => `babytrack://pass/${code}`;

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** A clean pass code from typed or pasted input, or null. */
export function normalisePassCode(input) {
  if (input === null || input === undefined) return null;
  let raw = String(input).trim();
  if (!raw) return null;
  const link = raw.match(LINK_RE);
  if (link) raw = safeDecode(link[1]);
  const code = raw.replace(/[\s-]+/g, '').toUpperCase();
  return CODE_RE.test(code) ? code : null;
}

// --- Stash (localStorage) ---------------------------------------------------

const stashListeners = new Set();

function notifyStash() {
  stashListeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* one bad listener must not break the rest */
    }
  });
}

/** Subscribe to stash changes; returns an unsubscribe function. */
export function subscribeStashedPassCode(fn) {
  stashListeners.add(fn);
  return () => stashListeners.delete(fn);
}

/** Keep a code for redemption after sign-in. Returns the clean code, or null. */
export function stashPassCode(input) {
  const code = normalisePassCode(input);
  if (!code) return null;
  try {
    localStorage.setItem(PASS_STASH_KEY, JSON.stringify({ code, savedAt: Date.now() }));
  } catch {
    /* storage blocked — the code can still be typed in Settings */
  }
  notifyStash();
  return code;
}

/** The stashed code, or null (none, unreadable, or older than 30 days). */
export function readStashedPassCode() {
  try {
    const raw = localStorage.getItem(PASS_STASH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const code = normalisePassCode(parsed?.code);
    if (!code) return null;
    const savedAt = Number(parsed.savedAt);
    if (Number.isFinite(savedAt) && Date.now() - savedAt > STASH_MAX_AGE_MS) return null;
    return code;
  } catch {
    return null;
  }
}

export function clearStashedPassCode() {
  try {
    localStorage.removeItem(PASS_STASH_KEY);
  } catch {
    /* nothing to clear */
  }
  notifyStash();
}

// --- Redeem -----------------------------------------------------------------

const inflight = new Map(); // code → promise, so one code is never POSTed twice at once

async function postRedeem(code) {
  const res = await silentFetchDetailed('/subscription/referral/redeem', 'POST', { code });
  const data = res?.data;
  if (res?.ok && data?.ok === true) {
    return { ok: true, pass: data.pass && typeof data.pass === 'object' ? data.pass : null };
  }
  if (res?.ok && data?.ok === false) {
    return { ok: false, reason: typeof data.reason === 'string' && data.reason ? data.reason : 'invalid' };
  }
  // No definitive answer: offline, timeout, 401, 5xx, or a route not deployed.
  return { ok: false, reason: !res?.status || isOffline() ? 'network' : 'error' };
}

/** True when a redeem result is final — the code should not be tried again. */
export function isDefinitivePassResult(result) {
  return !!result && (result.ok || (result.reason !== 'network' && result.reason !== 'error'));
}

/**
 * POST /subscription/referral/redeem. Resolves { ok: true, pass: { endsAt } }
 * or { ok: false, reason } — the server's reasons (invalid, own_code,
 * already_redeemed, already_premium, cap_reached) or 'network' / 'error' when
 * no answer came back. Never rejects, never toasts (callers word the
 * outcome). A definitive answer for the stashed code also clears the stash.
 */
export async function redeemPassCode(input) {
  const code = normalisePassCode(input);
  if (!code) return { ok: false, reason: 'invalid' };
  if (inflight.has(code)) return inflight.get(code);

  const request = postRedeem(code)
    .catch(() => ({ ok: false, reason: 'error' }))
    .then((result) => {
      if (isDefinitivePassResult(result) && readStashedPassCode() === code) clearStashedPassCode();
      return result;
    })
    .finally(() => inflight.delete(code));
  inflight.set(code, request);
  return request;
}

// --- Copy ---------------------------------------------------------------------

const REASON_MESSAGES = {
  invalid: 'That code doesn’t match a BabyTrack pass. Check it and try again.',
  own_code: 'That’s your own pass code. Send it to a friend instead.',
  already_redeemed: 'You’ve already used a Premium pass. Each person can redeem one.',
  already_premium: 'You already have Premium, so there’s nothing to redeem.',
  cap_reached: 'This pass code has been used as many times as it can be.',
  network: 'We couldn’t reach BabyTrack. Check your connection and try again.',
  error: 'Something went wrong. Please try again in a moment.',
};

/** One sentence for a redeem outcome's reason. */
export function passReasonMessage(reason) {
  return REASON_MESSAGES[reason] || REASON_MESSAGES.error;
}

/** Shown before any request when the input isn't a code or a pass link. */
export const PASS_INPUT_HINT = 'Enter the 8-character code from your friend’s pass, or paste their link.';

/** "4 November 2026" for a pass end (epoch ms or ISO string), or null. */
export function formatPassEnd(endsAt) {
  const ms = typeof endsAt === 'number' ? endsAt : Date.parse(endsAt || '');
  return Number.isFinite(ms) ? format(new Date(ms), 'd MMMM yyyy') : null;
}

/** The message the referrer shares (docs/v150-spec.md §3.7). */
export function passShareText({ babyName, url }) {
  const name = String(babyName || '').trim();
  const whose = name ? `${name}'s` : "my baby's";
  return `I use BabyTrack to keep track of ${whose} sleep, feeds and nappies. Here's a free 30-day Premium pass: ${url}`;
}
